import {
  ListenableSubscribable,
  SubscribableReconnectingSocket,
  SubscribableReconnectingSocketEvent,
  Subscription,
} from '@benzinga/subscribable';

import {
  FeedId,
  NewsfeedFieldType,
  NewsBZQLQuery,
  NewsBZQLRequestQuery,
  QueryId,
  SocketStory,
  RequiredSocketStoryFields,
} from './entities';
import { SafeError, SafePromise, SafeType, safeResilient } from '@benzinga/safe-await';
import { NewsSession } from './session';
import { Session } from '@benzinga/session';
import { AdvancedNewsEnvironment } from './environment';
import { DateTime } from 'luxon';
import { expressionConvert } from './internal-utils';
import { debounce } from '@benzinga/utils';

export interface NewsSocketSubscriptionEvent {
  feedId: string;
  query?: NewsBZQLQuery;
  type: 'news:subscribe';
}
export interface NewsSocketStoryEvent {
  feedIds: string[];
  story: SocketStory;
  type: 'news:subscription_stories';
}

export interface NewsSocketQueryTimeout {
  query?: NewsBZQLQuery;
  queryId: string;
  type: 'news:query_timeout';
}

export interface NewsSocketUnsubscribeEvent {
  feedId: string;
  type: 'news:unsubscribe';
}

export interface NewsSocketQueryEvent {
  query?: NewsBZQLQuery;
  queryId: string;
  stories: SocketStory[];
  type: 'news:query_stories';
}

export interface NewsSocketReconnectEvent {
  type: 'news:reconnected' | 'news:disconnected';
}
export interface NewsSocketSubscriptionErrorEvent extends Required<ErrorResponse> {
  feedIds?: string[];
  responseType: 'news_sub_results' | 'news_sub_confirm' | 'news_unsub_confirm';
  type: 'news:socket_errors';
}

export interface NewsSocketQueryErrorEvent extends Required<ErrorResponse> {
  queryId?: string;
  responseType: 'news_query_resp';
  type: 'news:socket_errors';
}

export type NewsSocketEvent =
  | NewsSocketStoryEvent
  | NewsSocketSubscriptionEvent
  | NewsSocketUnsubscribeEvent
  | NewsSocketQueryEvent
  | NewsSocketQueryTimeout
  | NewsSocketSubscriptionErrorEvent
  | NewsSocketQueryErrorEvent
  | NewsSocketReconnectEvent;

export class NewsSocket extends ListenableSubscribable<NewsSocketEvent> {
  private session: Session;
  private socket: SubscribableReconnectingSocket<string>;
  private socketSubscription?: Subscription<SubscribableReconnectingSocket<string>>;

  private feedIdQueryMapping: Map<FeedId, NewsBZQLQuery> = new Map();
  private queryIdQueryMapping: Map<QueryId, NewsBZQLQuery> = new Map();
  private nextQueryId = 0;
  private nextFeedId = 0;

  private state: 'running' | 'stopped' = 'stopped';
  private debounceStop = debounce(this.stop, 1000);
  private keepSocketAlive: boolean;

  constructor(session: Session) {
    super();
    this.session = session;
    this.socket = new SubscribableReconnectingSocket(session.getEnvironment(AdvancedNewsEnvironment).socketUrl);
    this.keepSocketAlive = session.getEnvironment(AdvancedNewsEnvironment).keepSocketAlive === 'true';
    if (this.keepSocketAlive) {
      this.open();
    }
  }

  public query = (data: Partial<NewsBZQLQuery>): SafePromise<SocketStory[]> => {
    const requiredFields: NewsfeedFieldType[] = [
      'CreatedAt',
      'ID',
      'NodeID',
      'UpdatedAt',
      'VersionID',
    ] as RequiredSocketStoryFields;
    const query = {
      fields: data.fields ? Array.from(new Set([...data.fields, ...requiredFields])) : requiredFields,
      limit: data.limit ?? 100,
      offset: data.offset ?? 0,
      sort: data.sort ?? ({ CreatedAt: -1 } as Record<NewsfeedFieldType, -1 | 1>),
      where: data.where ?? null,
    };

    const subscription = [] as ReturnType<typeof this.subscribe>[];
    const stories = safeResilient(
      () =>
        new Promise<SafeType<SocketStory[]>>(resolve => {
          const queryId = `${this.nextQueryId++}`;
          this.queryIdQueryMapping.set(queryId, query);
          subscription.push(
            this.subscribe(event => {
              switch (event.type) {
                case 'news:query_stories':
                  if (event.queryId === queryId) {
                    subscription.forEach(s => s.unsubscribe());
                    subscription.length = 0;
                    resolve({ ok: event.stories });
                  }
                  break;
                case 'news:socket_errors':
                  if (event.responseType === 'news_query_resp' && event.queryId === queryId) {
                    subscription.forEach(s => s.unsubscribe());
                    subscription.length = 0;
                    resolve({ err: new SafeError(event.errors[0].value, event.errors[0].code, event.errors) });
                  }
                  break;
              }
            }),
          );
          this.socket.sendObject<Request>({
            data: {
              ...query,
              id: queryId,
              where: data.where?.toBZQL(expressionConvert(this.session) as any) ?? null,
            },
            type: 'news_query',
          });
        }),
      {
        delayMultiple: 1.5,
        delayOffset: 30000,
        maxDelay: 600000,
        retryOnError: false,
      },
    )();
    stories.then(() => subscription.forEach(s => s.unsubscribe()));

    return stories;
  };

  public createFeed = (query: Partial<NewsBZQLQuery>, jumpedToDate?: DateTime): NewsSession => {
    const definedQuery: NewsBZQLQuery = {
      fields: query.fields ?? [],
      limit: query.limit ?? 100,
      offset: query.offset ?? 0,
      sort: query.sort ?? {},
      where: query.where ?? null,
    };

    const feedId = `${this.nextFeedId++}`;
    const newFeed = new NewsSession(this, definedQuery, feedId, jumpedToDate);
    this.feedIdQueryMapping.set(feedId, definedQuery);
    return newFeed;
  };

  public startFeed = (newsfeedSession: NewsSession): void => {
    const { fields, sort, ...query } = newsfeedSession.getQuery();
    const requiredFields: NewsfeedFieldType[] = [
      'CreatedAt',
      'ID',
      'NodeID',
      'UpdatedAt',
      'VersionID',
    ] as RequiredSocketStoryFields;
    this.socket.sendObject<Request>({
      data: {
        ...query,
        fields: fields ? Array.from(new Set([...fields, ...requiredFields])) : requiredFields,
        id: newsfeedSession.getFeedId(),
        sort: Object.keys(sort).length === 0 ? undefined : sort,
        where: query.where?.toBZQL(expressionConvert(this.session) as any) ?? null,
      },
      type: 'news_sub',
    });
  };

  public endFeed = (feed: NewsSession): void => {
    const feedId = feed.getFeedId();
    this.socket.sendObject<Request>({
      data: { query_ids: [feedId] },
      type: 'news_unsub',
    });
  };

  public getState = (): NewsSocket['state'] => this.state;

  protected onFirstSubscription(): void {
    if (this.keepSocketAlive) {
      return;
    }
    this.open();
  }

  protected onZeroSubscriptions(): void {
    if (this.keepSocketAlive) {
      return;
    }
    this.debounceStop();
  }

  private stop(): void {
    if (this.hasSubscribers()) return;
    this.socket.close();
    this.socketSubscription?.unsubscribe();
    this.socketSubscription = undefined;
    this.state = 'stopped';
  }

  private open = (): void => {
    if (this.socketSubscription === undefined) {
      this.socketSubscription = this.socket.subscribe(this.onMessage);
    }
    this.socket.open();
    this.state = 'running';
  };

  private processErr = (msg: Response): boolean => {
    if (msg.errors !== undefined) {
      switch (msg.type) {
        case 'news_query_resp': {
          const errors = msg.errors;
          msg.data.matched_queries.forEach(queryId => {
            this.dispatch({
              errors,
              queryId: queryId,
              responseType: msg.type,
              type: 'news:socket_errors',
            });
          });
          break;
        }
        case 'news_sub_results':
          this.dispatch({
            errors: msg.errors,
            feedIds: msg.data?.matched_queries,
            responseType: msg.type,
            type: 'news:socket_errors',
          });
          break;
        case 'news_sub_confirm':
        case 'news_unsub_confirm':
          this.dispatch({
            errors: msg.errors,
            feedIds: [msg.data.id],
            responseType: msg.type,
            type: 'news:socket_errors',
          });
          break;
      }
      return false;
    }
    return true;
  };

  private onMessage = (event: SubscribableReconnectingSocketEvent<string>): void => {
    switch (event.type) {
      case 'response': {
        const msg: Response = JSON.parse(event.msg);
        if (this.processErr(msg)) {
          switch (msg.type) {
            case 'news_sub_confirm':
              this.dispatch({
                feedId: msg.data.id,
                query: this.feedIdQueryMapping.get(msg.data.id),
                type: 'news:subscribe',
              });
              this.feedIdQueryMapping.delete(msg.data.id);
              break;
            case 'news_unsub_confirm':
              this.dispatch({
                feedId: msg.data.id,
                type: 'news:unsubscribe',
              });
              break;
            case 'news_sub_results':
              (msg.data.content ?? []).forEach(story => {
                this.dispatch({ feedIds: msg.data.matched_queries, story, type: 'news:subscription_stories' });
              });
              break;
            case 'news_query_resp': {
              const content = msg.data.content ?? [];
              msg.data.matched_queries.forEach(queryId => {
                this.dispatch({
                  query: this.queryIdQueryMapping.get(queryId),
                  queryId: queryId,
                  stories: content,
                  type: 'news:query_stories',
                });
                this.queryIdQueryMapping.delete(queryId);
              });

              break;
            }
          }
        }
        break;
      }
      case 'reconnected':
        this.dispatch({ type: 'news:reconnected' });
        this.queryIdQueryMapping.forEach((query, queryId) => {
          this.socket.sendObject<Request>({
            data: { ...query, id: queryId, where: query.where?.toBZQL(expressionConvert(this.session) as any) ?? null },
            type: 'news_query',
          });
        });
        break;
      case 'disconnected':
        this.dispatch({ type: 'news:disconnected' });
    }
  };
}

interface SubscribeRequest {
  data: NewsBZQLRequestQuery & { id: string };
  type: 'news_sub';
}

interface UnsubscribeRequest {
  data: { query_ids: string[] };
  type: 'news_unsub';
}

interface QueryRequest {
  data: NewsBZQLRequestQuery & { id: string };
  type: 'news_query';
}

type Request = QueryRequest | SubscribeRequest | UnsubscribeRequest;

interface ErrorResponse {
  errors?: {
    code: string;
    id: string;
    value: string;
  }[];
}
interface QueryResponse extends ErrorResponse {
  data: {
    content: SocketStory[] | null;
    matched_queries: string[];
  };
  result: {
    code: string;
    msg: string;
  };
  type: 'news_query_resp' | 'news_sub_results';
}

interface SubscriptionResponse extends ErrorResponse {
  data: NewsBZQLRequestQuery & { id: string };
  result: {
    code: string;
    msg: string;
  };
  type: 'news_sub_confirm';
}

interface UnsubscriptionResponse extends ErrorResponse {
  data: NewsBZQLRequestQuery & { id: string };
  result: {
    code: string;
    msg: string;
  };
  type: 'news_unsub_confirm';
}

type Response = QueryResponse | SubscriptionResponse | UnsubscriptionResponse;
